import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Store,
  ShoppingCart,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronRight,
  TrendingUp,
  Users,
  DollarSign,
  Package,
  BarChart3,
  Bell,
  Globe,
  CheckCircle,
  XCircle,
  RefreshCw,
  Trash2,
  Eye,
  Crown,
  Calendar,
  Euro,
  Loader2,
  Save,
  AlertTriangle,
  Mail,
  Percent,
  Clock,
  ToggleLeft,
  ToggleRight,
  Plus,
  Edit,
  Key,
  ExternalLink,
  FileText,
  Download,
  Search,
  Filter,
  MoreVertical,
  Copy,
  Lock,
  UserCog,
  ListChecks,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAdmin } from '../context/AdminContext';
import { adminAPI } from '../lib/api';
import { formatPrice } from '../lib/utils';
import { toast } from 'sonner';

// Admin Layout
export const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { admin, logout } = useAdmin();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
    toast.success('Uitgelogd');
  };

  const navItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/dashboard/restaurants', icon: Store, label: 'Restaurants' },
    { path: '/admin/dashboard/subscriptions', icon: CreditCard, label: 'Abonnementen' },
    { path: '/admin/dashboard/invoices', icon: Euro, label: 'Facturen' },
    { path: '/admin/dashboard/domains', icon: Globe, label: 'Domeinen' },
    { path: '/admin/dashboard/seo', icon: Search, label: 'SEO' },
    { path: '/admin/dashboard/blog', icon: FileText, label: 'Blog' },
    { path: '/admin/dashboard/orders', icon: ShoppingCart, label: 'Bestellingen' },
    { path: '/admin/dashboard/onboarding', icon: ListChecks, label: 'Onboarding' },
    { path: '/admin/dashboard/legal', icon: Lock, label: 'Voorwaarden' },
    { path: '/admin/dashboard/settings', icon: Settings, label: 'Instellingen' },
  ];

  return (
    <div className="min-h-screen bg-gray-900" data-testid="admin-layout">
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside className={`fixed top-0 left-0 h-full w-64 bg-gray-800 border-r border-gray-700 z-50 transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6 border-b border-gray-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">FoodFlows</h1>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/admin/dashboard' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isActive
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-gray-400 hover:bg-gray-700 hover:text-white'
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="shrink-0 p-4 border-t border-gray-700">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center">
              <span className="text-emerald-400 font-medium text-sm">
                {admin?.email?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">Platform Admin</p>
              <p className="text-gray-400 text-xs truncate">{admin?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-gray-400 hover:bg-gray-700 hover:text-white rounded-xl transition-all"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Uitloggen</span>
          </button>
        </div>
      </aside>

      <div className="lg:ml-64">
        <header className="bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between lg:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-emerald-400" />
            <span className="font-bold text-white">Admin</span>
          </div>
          <button className="p-2 text-gray-400 hover:text-white">
            <Bell className="w-6 h-6" />
          </button>
        </header>

        <main className="p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

// Admin Dashboard - Focus on Subscription Revenue
export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await adminAPI.getStats();
      setStats(response.data);
    } catch (error) {
      toast.error('Kon statistieken niet laden');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">Platform overzicht en abonnement statistieken</p>
      </div>

      {/* Subscription Revenue - Main Focus */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Euro className="w-6 h-6 text-white" />
            </div>
            <TrendingUp className="w-5 h-5 text-emerald-200" />
          </div>
          <p className="text-3xl font-bold text-white">
            {formatPrice(stats?.subscription_revenue?.mrr || 0, 'EUR')}
          </p>
          <p className="text-emerald-100 text-sm mt-1">Maandelijkse Omzet (MRR)</p>
        </div>

        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-purple-400" />
            </div>
            <TrendingUp className="w-5 h-5 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-white">
            {formatPrice(stats?.subscription_revenue?.total || 0, 'EUR')}
          </p>
          <p className="text-gray-400 text-sm mt-1">Totale Abonnement Omzet</p>
        </div>

        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6 text-blue-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.restaurants?.paying || 0}</p>
          <p className="text-gray-400 text-sm mt-1">Betalende Klanten</p>
        </div>

        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Store className="w-6 h-6 text-amber-400" />
            </div>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.restaurants?.total || 0}</p>
          <p className="text-gray-400 text-sm mt-1">Totaal Restaurants</p>
        </div>
      </div>

      {/* Subscribers by Plan */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Abonnees per Plan</h3>
          <div className="space-y-4">
            {[
              { id: 'starter', name: 'FoodFlows', color: 'emerald', price: 49.00 },
              { id: 'enterprise', name: 'Enterprise', color: 'purple', price: null },
            ].map((plan) => (
              <div key={plan.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full bg-${plan.color}-500`}></div>
                  <span className="text-white font-medium">{plan.name}</span>
                  <span className="text-gray-400 text-sm">
                    {plan.price ? `€${plan.price.toFixed(2)}/maand · incl. 12% BTW` : 'Op aanvraag'}
                  </span>
                </div>
                <span className="text-xl font-bold text-white">
                  {stats?.restaurants?.by_plan?.[plan.id] || 0}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Bestellingen per Status</h3>
          <div className="space-y-3">
            {stats?.orders?.by_status && Object.entries(stats.orders.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <span className="text-gray-400 capitalize">{status}</span>
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Vandaag</span>
              <span className="text-2xl font-bold text-emerald-400">{stats?.orders?.today || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Snelle Acties</h3>
        <div className="grid sm:grid-cols-4 gap-4">
          <Link to="/admin/dashboard/subscriptions" className="p-4 bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors">
            <CreditCard className="w-6 h-6 text-emerald-400 mb-2" />
            <p className="text-white font-medium">Abonnementen</p>
            <p className="text-gray-400 text-sm">Beheer inkomsten</p>
          </Link>
          <Link to="/admin/dashboard/restaurants" className="p-4 bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors">
            <Store className="w-6 h-6 text-blue-400 mb-2" />
            <p className="text-white font-medium">Restaurants</p>
            <p className="text-gray-400 text-sm">Beheer klanten</p>
          </Link>
          <Link to="/admin/dashboard/domains" className="p-4 bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors">
            <Globe className="w-6 h-6 text-purple-400 mb-2" />
            <p className="text-white font-medium">Domeinen</p>
            <p className="text-gray-400 text-sm">DNS beheer</p>
          </Link>
          <Link to="/admin/dashboard/settings" className="p-4 bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors">
            <Settings className="w-6 h-6 text-amber-400 mb-2" />
            <p className="text-white font-medium">Instellingen</p>
            <p className="text-gray-400 text-sm">Platform config</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Admin Restaurants Page
export const AdminRestaurantsPage = () => {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [bedrijf, setBedrijf] = useState({});
  const [juridisch, setJuridisch] = useState(null);
  const [emailInvoer, setEmailInvoer] = useState('');
  const [onboarding, setOnboarding] = useState(null);
  const [emailBezig, setEmailBezig] = useState(false);
  const [emailLog, setEmailLog] = useState([]);
  const [bevestigNotitie, setBevestigNotitie] = useState('');
  const [bedrijfOpslaan, setBedrijfOpslaan] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [newPassword, setNewPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [newRestaurant, setNewRestaurant] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    address: '',
    subscription_plan: 'starter'
  });

  useEffect(() => {
    fetchRestaurants();
  }, []);

  // De lijst bevat niet alle velden; het detail-endpoint vult de
  // bedrijfsgegevens aan en zegt welke daarvan ontbreken.
  const openDetails = async (restaurant) => {
    setSelectedRestaurant(restaurant);
    setShowDetails(true);
    try {
      const { data } = await adminAPI.getRestaurantDetails(restaurant.id);
      setSelectedRestaurant(data);
      setBedrijf({
        name: data.name || '',
        phone: data.phone || '',
        address: data.address || '',
        postal_code: data.postal_code || '',
        city: data.city || '',
        vat_number: data.vat_number || '',
      });
    } catch (e) {
      toast.error('Kon de restaurantgegevens niet laden');
    }
    try {
      const { data } = await adminAPI.getRestaurantLegal(restaurant.id);
      setJuridisch(data);
    } catch (e) {
      setJuridisch(null);
    }
    setEmailInvoer(restaurant.email || '');
    setBevestigNotitie('');
    laadEmailLog(restaurant.id);
    try {
      const { data } = await adminAPI.getRestaurantOnboarding(restaurant.id);
      setOnboarding(data);
    } catch (e) {
      setOnboarding(null);
    }
  };

  const laadEmailLog = async (id) => {
    try {
      const { data } = await adminAPI.getRestaurantEmailLog(id);
      setEmailLog(data.entries || []);
    } catch (e) {
      setEmailLog([]);
    }
  };

  const ververs = async (id) => {
    const { data } = await adminAPI.getRestaurantDetails(id);
    setSelectedRestaurant(data);
    setEmailInvoer(data.email || '');
    laadEmailLog(id);
    fetchRestaurants();
  };

  const wijzigEmail = async () => {
    const nieuw = emailInvoer.trim().toLowerCase();
    if (!nieuw || nieuw === (selectedRestaurant.email || '').toLowerCase()) return;
    if (!window.confirm(
      `Het e-mailadres is ook de inlognaam.\n\n` +
      `${selectedRestaurant.email}\n    wordt\n${nieuw}\n\n` +
      `Beide adressen krijgen bericht en het nieuwe moet opnieuw bevestigd worden. Doorgaan?`
    )) return;
    setEmailBezig(true);
    try {
      const { data } = await adminAPI.changeRestaurantEmail(selectedRestaurant.id, nieuw);
      toast.success(data.message, { duration: 7000 });
      if (data.stripe_updated) toast.success('Stripe-klant bijgewerkt');
      await ververs(selectedRestaurant.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wijzigen mislukt');
    } finally {
      setEmailBezig(false);
    }
  };

  const verstuurVerificatieOpnieuw = async () => {
    setEmailBezig(true);
    try {
      const { data } = await adminAPI.resendRestaurantVerification(selectedRestaurant.id);
      toast.success(data.message);
      laadEmailLog(selectedRestaurant.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setEmailBezig(false);
    }
  };

  const markeerBevestigd = async () => {
    setEmailBezig(true);
    try {
      const { data } = await adminAPI.markRestaurantVerified(
        selectedRestaurant.id, bevestigNotitie);
      toast.success(data.message);
      setBevestigNotitie('');
      await ververs(selectedRestaurant.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Markeren mislukt');
    } finally {
      setEmailBezig(false);
    }
  };

  const zetDemo = async (aan) => {
    try {
      await adminAPI.updateRestaurant(selectedRestaurant.id, { is_demo: aan });
      toast.success(aan
        ? 'Dit restaurant is nu een demo: schrijfacties worden geweigerd.'
        : 'Demo uitgezet: dit restaurant kan weer gewijzigd worden.');
      const { data } = await adminAPI.getRestaurantDetails(selectedRestaurant.id);
      setSelectedRestaurant(data);
      fetchRestaurants();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wijzigen mislukt');
    }
  };

  const bewaarBedrijfsgegevens = async () => {
    setBedrijfOpslaan(true);
    try {
      // Lege velden niet meesturen: de backend negeert null, en zo blijft een
      // veld dat de admin bewust leeg laat gewoon leeg in plaats van te
      // struikelen over de btw-validatie.
      const payload = {};
      Object.entries(bedrijf).forEach(([k, v]) => {
        if (String(v || '').trim()) payload[k] = String(v).trim();
      });
      await adminAPI.updateRestaurant(selectedRestaurant.id, payload);
      toast.success('Bedrijfsgegevens opgeslagen');
      const { data } = await adminAPI.getRestaurantDetails(selectedRestaurant.id);
      setSelectedRestaurant(data);
      fetchRestaurants();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg?.replace('Value error, ', '')).join(' · '));
      } else {
        toast.error(detail || 'Opslaan mislukt');
      }
    } finally {
      setBedrijfOpslaan(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await adminAPI.getRestaurants(0, 500);
      setRestaurants(response.data.restaurants || response.data);
    } catch (error) {
      toast.error('Kon restaurants niet laden');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (id, overrideReason = null) => {
    try {
      const response = await adminAPI.toggleRestaurantStatus(id, overrideReason);
      toast.success(response.data.message);
      if (response.data.overridden) {
        toast.warning('Bewust geactiveerd terwijl er nog stappen ontbraken — vastgelegd in het logboek',
                      { duration: 8000 });
      }
      fetchRestaurants();
      if (selectedRestaurant?.id === id) ververs(id);
    } catch (error) {
      const detail = error.response?.data?.detail;
      // De backend weigert activeren zolang de verplichte stappen niet af zijn,
      // en stuurt mee wát er ontbreekt. Pas na een expliciete toelichting gaat
      // het alsnog door, en dan gaat die toelichting het logboek in.
      if (detail?.needs_override) {
        const reden = window.prompt(
          `${detail.message}\n\nNog niet af:\n` +
          detail.missing.map(m => `  · ${m}`).join('\n') +
          `\n\nWil je toch activeren? Vul dan kort in waarom (minstens 5 tekens).` +
          `\nDat wordt vastgelegd bij dit restaurant.`
        );
        if (reden && reden.trim().length >= 5) {
          toggleStatus(id, reden.trim());
        } else if (reden !== null) {
          toast.error('Geen toelichting ingevuld — niet geactiveerd');
        }
        return;
      }
      toast.error(typeof detail === 'string' ? detail : 'Kon status niet wijzigen');
    }
  };

  const handleCreateRestaurant = async () => {
    if (!newRestaurant.name || !newRestaurant.email || !newRestaurant.password) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    setCreating(true);
    try {
      await adminAPI.createRestaurant(newRestaurant);
      toast.success('Restaurant aangemaakt!');
      setShowCreateDialog(false);
      setNewRestaurant({ name: '', email: '', password: '', phone: '', address: '', subscription_plan: 'starter' });
      fetchRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon restaurant niet aanmaken');
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Wachtwoord moet minimaal 6 tekens zijn');
      return;
    }
    try {
      await adminAPI.resetRestaurantPassword(selectedRestaurant.id, newPassword);
      toast.success('Wachtwoord gereset!');
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Kon wachtwoord niet resetten');
    }
  };

  const handleLoginAs = async (restaurant) => {
    try {
      const response = await adminAPI.loginAsRestaurant(restaurant.id);
      // Pass the impersonation token to the new tab via URL query param —
      // AuthContext reads it on that tab's own initial load and fetches the
      // profile itself (see impersonate_token handling in AuthContext.js).
      // This avoids depending on the new tab reading localStorage that this
      // (different) tab/window just wrote, which is a cross-context race —
      // writing it here too is a harmless fallback, not the primary path.
      localStorage.setItem('restaurant_token', response.data.token);
      localStorage.setItem('restaurant_data', JSON.stringify(response.data.restaurant));
      toast.success(`Ingelogd als ${restaurant.name}`);
      window.open(`/dashboard?impersonate_token=${encodeURIComponent(response.data.token)}`, '_blank');
    } catch (error) {
      toast.error('Kon niet inloggen als restaurant');
    }
  };

  const handleUpdateSubscription = async (restaurantId, planId) => {
    try {
      await adminAPI.updateRestaurantSubscription(restaurantId, planId);
      toast.success('Abonnement bijgewerkt');
      fetchRestaurants();
      setShowDetails(false);
    } catch (error) {
      toast.error('Kon abonnement niet bijwerken');
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const handleDeleteRestaurant = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await adminAPI.deleteRestaurant(deleteTarget.id);
      toast.success(`Restaurant "${deleteTarget.name}" verwijderd`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon restaurant niet verwijderen');
    } finally {
      setDeleting(false);
    }
  };

  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         r.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlan = filterPlan === 'all' || r.subscription_plan === filterPlan;
    return matchesSearch && matchesPlan;
  });

  const planColors = {
    free: 'bg-gray-500/20 text-gray-400',
    starter: 'bg-blue-500/20 text-blue-400',
    enterprise: 'bg-purple-500/20 text-purple-400',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-restaurants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Restaurants</h1>
          <p className="text-gray-400 mt-1">{restaurants.length} restaurants totaal</p>
        </div>
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nieuw Restaurant
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Zoek op naam of email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-gray-800 border-gray-700 text-white"
          />
        </div>
        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4"
        >
          <option value="all">Alle Plannen</option>
          <option value="starter">FoodFlows (€49)</option>
          <option value="enterprise">Enterprise (op aanvraag)</option>
        </select>
      </div>

      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Restaurant</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Abonnement</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Bestellingen</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Acties</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {filteredRestaurants.map((restaurant) => (
              <tr key={restaurant.id} className="hover:bg-gray-700/30">
                <td className="px-6 py-4">
                  <div>
                    <p className="text-white font-medium">{restaurant.name}</p>
                    <div className="flex items-center gap-1.5">
                      <p className="text-gray-400 text-sm">{restaurant.email}</p>
                      {restaurant.email_verified === false ? (
                        <span title="E-mailadres niet bevestigd"
                              data-testid={`onbevestigd-${restaurant.id}`}
                              className="text-amber-400 flex-shrink-0">
                          <AlertTriangle className="w-3.5 h-3.5" />
                        </span>
                      ) : (
                        <span title="E-mailadres bevestigd" className="text-emerald-500 flex-shrink-0">
                          <CheckCircle className="w-3.5 h-3.5" />
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${planColors[restaurant.subscription_plan] || planColors.free}`}>
                    {restaurant.subscription_plan || 'free'}
                  </span>
                </td>
                <td className="px-6 py-4 text-white">{restaurant.order_count || 0}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    restaurant.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                  }`}>
                    {restaurant.is_active !== false ? 'Actief' : 'Inactief'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDetails(restaurant)}
                      className="text-gray-400 hover:text-white"
                      title="Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleLoginAs(restaurant)}
                      className="text-gray-400 hover:text-emerald-400"
                      title="Inloggen als"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setSelectedRestaurant(restaurant); setShowPasswordDialog(true); }}
                      className="text-gray-400 hover:text-amber-400"
                      title="Wachtwoord resetten"
                    >
                      <Key className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleStatus(restaurant.id)}
                      className="text-gray-400 hover:text-white"
                      title={restaurant.is_active !== false ? 'Deactiveren' : 'Activeren'}
                    >
                      {restaurant.is_active !== false ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(restaurant)}
                      className="text-gray-400 hover:text-red-400"
                      title="Restaurant verwijderen"
                      data-testid={`delete-restaurant-${restaurant.slug || restaurant.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create Restaurant Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuw Restaurant Aanmaken</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300">Naam *</Label>
              <Input
                value={newRestaurant.name}
                onChange={(e) => setNewRestaurant({...newRestaurant, name: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Restaurant naam"
              />
            </div>
            <div>
              <Label className="text-gray-300">Email *</Label>
              <Input
                type="email"
                value={newRestaurant.email}
                onChange={(e) => setNewRestaurant({...newRestaurant, email: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="email@restaurant.be"
              />
            </div>
            <div>
              <Label className="text-gray-300">Wachtwoord *</Label>
              <Input
                type="password"
                value={newRestaurant.password}
                onChange={(e) => setNewRestaurant({...newRestaurant, password: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Minimaal 6 tekens"
              />
            </div>
            <div>
              <Label className="text-gray-300">Telefoon</Label>
              <Input
                value={newRestaurant.phone}
                onChange={(e) => setNewRestaurant({...newRestaurant, phone: e.target.value})}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="+32 123 456 789"
              />
            </div>
            <div>
              <Label className="text-gray-300">Abonnement</Label>
              <select
                value={newRestaurant.subscription_plan}
                onChange={(e) => setNewRestaurant({...newRestaurant, subscription_plan: e.target.value})}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2 mt-1"
              >
                <option value="starter">FoodFlows — €49/maand (excl. btw)</option>
                <option value="enterprise">Enterprise — Op aanvraag</option>
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuleren</Button>
            <Button onClick={handleCreateRestaurant} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restaurant Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              {selectedRestaurant?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedRestaurant && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Email</p>
                  <p className="text-white">{selectedRestaurant.email}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Telefoon</p>
                  <p className="text-white">{selectedRestaurant.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Slug</p>
                  <p className="text-white font-mono text-sm">{selectedRestaurant.slug}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Custom Domein</p>
                  <p className="text-white">{selectedRestaurant.custom_domain || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Bestellingen</p>
                  <p className="text-white">{selectedRestaurant.order_count || 0}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Aangemaakt</p>
                  <p className="text-white">{selectedRestaurant.created_at ? new Date(selectedRestaurant.created_at).toLocaleDateString('nl-BE') : '-'}</p>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-gray-300">Bedrijfsgegevens</Label>
                  {selectedRestaurant.email_verification_pending ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      E-mailadres nog niet bevestigd
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      E-mailadres bevestigd
                    </span>
                  )}
                </div>

                {selectedRestaurant.company_details_missing?.length > 0 && (
                  <div className="mb-3 flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
                       data-testid="bedrijfsgegevens-ontbreken">
                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-200 text-sm">
                      Dit restaurant heeft zich aangemeld voordat deze gegevens verplicht werden.
                      Nog niet ingevuld: {selectedRestaurant.company_details_missing.join(', ')}.
                      Je kunt ze hieronder aanvullen.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label className="text-gray-400 text-xs">Restaurantnaam</Label>
                    <Input value={bedrijf.name || ''} data-testid="bedrijf-naam"
                           onChange={(e) => setBedrijf({ ...bedrijf, name: e.target.value })}
                           className="bg-gray-700 border-gray-600 text-white" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-400 text-xs">Straat en huisnummer</Label>
                    <Input value={bedrijf.address || ''} data-testid="bedrijf-adres"
                           onChange={(e) => setBedrijf({ ...bedrijf, address: e.target.value })}
                           className="bg-gray-700 border-gray-600 text-white" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Postcode</Label>
                    <Input value={bedrijf.postal_code || ''} maxLength={4} data-testid="bedrijf-postcode"
                           onChange={(e) => setBedrijf({ ...bedrijf, postal_code: e.target.value })}
                           className="bg-gray-700 border-gray-600 text-white" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Gemeente</Label>
                    <Input value={bedrijf.city || ''} data-testid="bedrijf-gemeente"
                           onChange={(e) => setBedrijf({ ...bedrijf, city: e.target.value })}
                           className="bg-gray-700 border-gray-600 text-white" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Telefoonnummer</Label>
                    <Input value={bedrijf.phone || ''} data-testid="bedrijf-telefoon"
                           onChange={(e) => setBedrijf({ ...bedrijf, phone: e.target.value })}
                           className="bg-gray-700 border-gray-600 text-white" />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs">Btw-nummer</Label>
                    <Input value={bedrijf.vat_number || ''} placeholder="BE0123456749"
                           data-testid="bedrijf-btw"
                           onChange={(e) => setBedrijf({ ...bedrijf, vat_number: e.target.value })}
                           className="bg-gray-700 border-gray-600 text-white" />
                  </div>
                </div>
                <Button onClick={bewaarBedrijfsgegevens} disabled={bedrijfOpslaan}
                        data-testid="bedrijf-opslaan"
                        className="btn-primary mt-3 w-full">
                  {bedrijfOpslaan ? 'Opslaan…' : 'Bedrijfsgegevens opslaan'}
                </Button>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-300 mb-2 block">Onboarding</Label>
                {!onboarding && <p className="text-gray-500 text-sm">Laden…</p>}
                {onboarding && (
                  <div data-testid="onboarding-detail">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="h-1.5 bg-gray-700 rounded-full flex-1 overflow-hidden">
                        <div className={`h-full rounded-full ${
                          onboarding.ready_to_activate ? 'bg-emerald-500' : 'bg-amber-500'}`}
                          style={{ width: `${Math.round((onboarding.required_done / Math.max(onboarding.required_total, 1)) * 100)}%` }} />
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {onboarding.required_done}/{onboarding.required_total} verplicht
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      {onboarding.steps.map(s => (
                        <div key={s.id} className="flex items-center gap-2 text-sm">
                          {s.done
                            ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            : <XCircle className={`w-3.5 h-3.5 flex-shrink-0 ${s.required ? 'text-amber-400' : 'text-gray-600'}`} />}
                          <span className={s.done ? 'text-gray-500' : 'text-gray-300'}>
                            {s.label}
                            {!s.required && <span className="text-gray-600 text-xs"> · optioneel</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                    {onboarding.activation_override && (
                      <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
                        <p className="text-amber-200 text-sm">
                          Bewust geactiveerd terwijl {onboarding.activation_override.missing.length}{' '}
                          {onboarding.activation_override.missing.length === 1 ? 'stap' : 'stappen'} ontbraken.
                        </p>
                        <p className="text-amber-300/70 text-xs mt-1">
                          "{onboarding.activation_override.reason}" — {onboarding.activation_override.admin_email},{' '}
                          {new Date(onboarding.activation_override.at).toLocaleString('nl-BE')}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <Label className="text-gray-300">E-mailadres en verificatie</Label>
                  {selectedRestaurant.email_verification_pending ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                      Niet bevestigd
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      Bevestigd
                      {selectedRestaurant.email_verified_at
                        ? ` · ${new Date(selectedRestaurant.email_verified_at).toLocaleString('nl-BE')}`
                        : ' · van voor de verificatieplicht'}
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <Input value={emailInvoer} type="email" data-testid="admin-email-invoer"
                         onChange={(e) => setEmailInvoer(e.target.value)}
                         className="bg-gray-700 border-gray-600 text-white flex-1" />
                  <Button onClick={wijzigEmail} data-testid="admin-email-opslaan"
                          disabled={emailBezig || !emailInvoer.trim()
                                    || emailInvoer.trim().toLowerCase() === (selectedRestaurant.email || '').toLowerCase()}
                          className="btn-primary flex-shrink-0">
                    Wijzigen
                  </Button>
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  Dit is ook de inlognaam. Beide adressen krijgen bericht en het nieuwe
                  adres moet opnieuw bevestigd worden.
                </p>

                {selectedRestaurant.email_verification_pending && (
                  <div className="mt-3 space-y-2">
                    <Button variant="outline" onClick={verstuurVerificatieOpnieuw}
                            disabled={emailBezig} data-testid="admin-verificatie-opnieuw"
                            className="w-full">
                      <Mail className="w-4 h-4 mr-2" />
                      Verificatiemail opnieuw versturen
                    </Button>
                    <div className="flex gap-2">
                      <Input value={bevestigNotitie} data-testid="admin-bevestig-notitie"
                             onChange={(e) => setBevestigNotitie(e.target.value)}
                             placeholder="Hoe bevestigd? Bijv. telefonisch op 10/09"
                             className="bg-gray-700 border-gray-600 text-white flex-1" />
                      <Button variant="outline" onClick={markeerBevestigd}
                              disabled={emailBezig || bevestigNotitie.trim().length < 3}
                              data-testid="admin-markeer-bevestigd" className="flex-shrink-0">
                        Handmatig bevestigen
                      </Button>
                    </div>
                    <p className="text-amber-300/70 text-xs">
                      Handmatig bevestigen omzeilt de normale controle en gaat met je
                      toelichting het logboek in.
                    </p>
                  </div>
                )}

                {emailLog.length > 0 && (
                  <div className="mt-4">
                    <p className="text-gray-400 text-xs mb-1">Logboek ({emailLog.length})</p>
                    <div className="space-y-1 max-h-32 overflow-y-auto" data-testid="email-logboek">
                      {emailLog.map((r) => (
                        <div key={r.id} className="text-xs text-gray-400 bg-gray-700/40 rounded px-2 py-1">
                          <span className="text-gray-300">{r.action.replace(/_/g, ' ')}</span>
                          {r.old_email && <> · {r.old_email} → {r.new_email}</>}
                          {!r.old_email && r.new_email && <> · {r.new_email}</>}
                          <br />
                          <span className="text-gray-500">
                            {new Date(r.created_at).toLocaleString('nl-BE')} · door {r.admin_email}
                            {r.note ? ` · ${r.note}` : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-700 pt-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-gray-300">Demo-omgeving</Label>
                    <p className="text-gray-500 text-xs mt-1 max-w-sm">
                      Staat dit aan, dan weigert de backend elke schrijfactie voor dit
                      restaurant: menu, instellingen, bestellingen, bezorgzones. Ook via
                      de API, niet alleen in de knoppen.
                    </p>
                  </div>
                  <button
                    onClick={() => zetDemo(!selectedRestaurant.is_demo)}
                    data-testid="demo-toggle"
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium flex-shrink-0 border ${
                      selectedRestaurant.is_demo
                        ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                        : 'bg-gray-700 text-gray-300 border-gray-600'}`}>
                    {selectedRestaurant.is_demo ? 'Demo staat aan' : 'Demo staat uit'}
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-300 mb-2 block">Aanvaarde voorwaarden</Label>
                {!juridisch && (
                  <p className="text-gray-500 text-sm">Laden…</p>
                )}
                {juridisch && (
                  <div className="space-y-2" data-testid="juridische-acceptaties">
                    {juridisch.summary.map((d) => (
                      <div key={d.doc_type}
                           className="flex items-start justify-between gap-3 rounded-lg bg-gray-700/40 px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-white text-sm">{d.title}</p>
                          {d.accepted_at ? (
                            <p className="text-gray-400 text-xs">
                              versie {d.accepted_version} · {new Date(d.accepted_at).toLocaleString('nl-BE')}
                              {d.ip_address ? ` · ${d.ip_address}` : ''}
                            </p>
                          ) : (
                            <p className="text-gray-500 text-xs">nog niet aanvaard</p>
                          )}
                          {d.content_hash && (
                            <p className="text-gray-600 text-xs font-mono">
                              sha256 {d.content_hash.slice(0, 16)}…
                            </p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${
                          d.up_to_date
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                            : 'bg-amber-500/20 text-amber-300 border-amber-500/40'}`}>
                          {d.up_to_date ? 'actueel' : `moet ${d.current_version}`}
                        </span>
                      </div>
                    ))}
                    <p className="text-gray-500 text-xs">
                      {juridisch.history?.length || 0} acceptatie(s) in de geschiedenis bewaard.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-700 pt-4">
                <Label className="text-gray-300 mb-2 block">Abonnement Wijzigen</Label>
                <div className="flex gap-2">
                  <select
                    defaultValue={selectedRestaurant.subscription_plan || 'starter'}
                    onChange={(e) => handleUpdateSubscription(selectedRestaurant.id, e.target.value)}
                    className="flex-1 bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2"
                  >
                    <option value="starter">FoodFlows — €49/maand (excl. btw)</option>
                    <option value="enterprise">Enterprise — Op aanvraag</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-700">
                <Button
                  variant="outline"
                  onClick={() => handleLoginAs(selectedRestaurant)}
                  className="flex-1"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Inloggen als Restaurant
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(`/shop/${selectedRestaurant.slug}`, '_blank')}
                  className="flex-1"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Bekijk Webshop
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Wachtwoord Resetten
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-gray-400">
              Reset het wachtwoord voor <strong className="text-white">{selectedRestaurant?.name}</strong>
            </p>
            <div>
              <Label className="text-gray-300">Nieuw Wachtwoord</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white mt-1"
                placeholder="Minimaal 6 tekens"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPasswordDialog(false); setNewPassword(''); }}>
              Annuleren
            </Button>
            <Button onClick={handleResetPassword} className="bg-amber-600 hover:bg-amber-700">
              <Key className="w-4 h-4 mr-2" />
              Wachtwoord Resetten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Restaurant Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="w-5 h-5" />
              Restaurant verwijderen?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <p className="text-gray-300">
              Je staat op het punt om <strong className="text-white">{deleteTarget?.name}</strong> permanent te verwijderen.
            </p>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300">
              ⚠️ Dit verwijdert het restaurant én alle bijbehorende data (menu, bestellingen, klanten, betalingen). <b>Deze actie kan niet ongedaan gemaakt worden.</b>
            </div>
            <p className="text-gray-400 text-sm">
              Typ <code className="bg-gray-700 px-2 py-0.5 rounded text-red-300">VERWIJDER</code> hieronder om te bevestigen.
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder="Typ VERWIJDER"
              data-testid="delete-restaurant-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }} data-testid="delete-restaurant-cancel">
              Annuleren
            </Button>
            <Button
              onClick={handleDeleteRestaurant}
              disabled={deleting || deleteConfirmText !== 'VERWIJDER'}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50"
              data-testid="delete-restaurant-confirm-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {deleting ? 'Bezig...' : 'Definitief verwijderen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Admin Subscriptions Page - Revenue Focus
export const AdminSubscriptionsPage = () => {
  const [revenue, setRevenue] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingPlan, setEditingPlan] = useState(null);
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [revenueRes, subscribersRes, plansRes] = await Promise.all([
        adminAPI.getSubscriptionRevenue(),
        adminAPI.getSubscribers(),
        adminAPI.getSubscriptionPlans(),
      ]);
      setRevenue(revenueRes.data);
      setSubscribers(subscribersRes.data);
      setPlans(plansRes.data.plans || []);
    } catch (error) {
      toast.error('Kon abonnement data niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlan = async () => {
    if (!editingPlan) return;
    setSavingPlan(true);
    try {
      await adminAPI.updateSubscriptionPlan(editingPlan.id, {
        name: editingPlan.name,
        price: parseFloat(editingPlan.price),
        description: editingPlan.description || '',
        features: editingPlan.features || [],
        order_limit: editingPlan.order_limit || 0,
        active: editingPlan.active !== false
      });
      toast.success('Plan bijgewerkt!');
      setShowPlanDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Kon plan niet bijwerken');
    } finally {
      setSavingPlan(false);
    }
  };

  const planColors = {
    free: 'bg-gray-500/20 text-gray-400',
    starter: 'bg-blue-500/20 text-blue-400',
    enterprise: 'bg-purple-500/20 text-purple-400',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-subscriptions-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Abonnementen & Omzet</h1>
        <p className="text-gray-400 mt-1">Beheer abonnementen en bekijk inkomsten</p>
      </div>

      {/* Revenue Overview */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6">
          <Euro className="w-8 h-8 text-emerald-100 mb-3" />
          <p className="text-3xl font-bold text-white">{formatPrice(revenue?.mrr || 0, 'EUR')}</p>
          <p className="text-emerald-100 text-sm">MRR (Maandelijks)</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <CreditCard className="w-8 h-8 text-purple-400 mb-3" />
          <p className="text-3xl font-bold text-white">{formatPrice(revenue?.total_revenue || 0, 'EUR')}</p>
          <p className="text-gray-400 text-sm">Totale Omzet</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <Calendar className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-3xl font-bold text-white">{formatPrice(revenue?.monthly_revenue || 0, 'EUR')}</p>
          <p className="text-gray-400 text-sm">Deze Maand</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <Crown className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-3xl font-bold text-white">{revenue?.active_subscriptions || 0}</p>
          <p className="text-gray-400 text-sm">Actieve Abonnementen</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'overview' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'
          }`}
        >
          Overzicht
        </button>
        <button
          onClick={() => setActiveTab('plans')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'plans' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'
          }`}
        >
          Plannen Beheren
        </button>
        <button
          onClick={() => setActiveTab('subscribers')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'subscribers' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'
          }`}
        >
          Abonnees ({subscribers.length})
        </button>
        <button
          onClick={() => setActiveTab('transactions')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'transactions' ? 'bg-emerald-500/20 text-emerald-400' : 'text-gray-400 hover:text-white'
          }`}
        >
          Transacties
        </button>
      </div>

      {activeTab === 'plans' && (
        <div className="space-y-4">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Plan</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Prijs</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Abonnees</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Functies</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {plans.map((plan) => (
                  <tr key={plan.id} className="hover:bg-gray-700/30">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${planColors[plan.id] || planColors.free}`}>
                          {plan.id}
                        </span>
                        <span className="text-white font-medium">{plan.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {plan.price == null || plan.contact_sales ? (
                        <span className="text-amber-300 font-semibold">Op aanvraag</span>
                      ) : (
                        <>
                          <span className="text-white font-bold text-lg">
                            {formatPrice(plan.price, 'EUR')}
                          </span>
                          <span className="text-gray-400 text-sm">/maand</span>
                          {plan.vat_rate ? (
                            <span className="block text-gray-500 text-xs">incl. {Math.round(plan.vat_rate * 100)}% BTW</span>
                          ) : null}
                        </>
                      )}
                    </td>
                    <td className="px-6 py-4 text-white">
                      {revenue?.subscribers_by_plan?.[plan.id] || 0}
                    </td>
                    <td className="px-6 py-4">
                      <ul className="text-gray-300 text-sm space-y-1">
                        {(plan.features || []).slice(0, 2).map((f, i) => (
                          <li key={i} className="truncate max-w-xs">• {f}</li>
                        ))}
                        {(plan.features || []).length > 2 && (
                          <li className="text-gray-500">+{plan.features.length - 2} meer</li>
                        )}
                      </ul>
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setEditingPlan({...plan}); setShowPlanDialog(true); }}
                        className="text-gray-400 hover:text-white"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Bewerken
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Edit Plan Dialog */}
          <Dialog open={showPlanDialog} onOpenChange={setShowPlanDialog}>
            <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Plan Bewerken: {editingPlan?.name}
                </DialogTitle>
              </DialogHeader>
              {editingPlan && (
                <div className="space-y-4 py-4">
                  <div>
                    <Label className="text-gray-300">Naam</Label>
                    <Input
                      value={editingPlan.name}
                      onChange={(e) => setEditingPlan({...editingPlan, name: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Prijs (EUR per maand)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingPlan.price}
                      onChange={(e) => setEditingPlan({...editingPlan, price: e.target.value})}
                      className="bg-gray-700 border-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Bestelling Limiet (0 = onbeperkt)</Label>
                    <Input
                      type="number"
                      value={editingPlan.order_limit || 0}
                      onChange={(e) => setEditingPlan({...editingPlan, order_limit: parseInt(e.target.value)})}
                      className="bg-gray-700 border-gray-600 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Functies (1 per regel)</Label>
                    <textarea
                      value={(editingPlan.features || []).join('\n')}
                      onChange={(e) => setEditingPlan({...editingPlan, features: e.target.value.split('\n').filter(f => f.trim())})}
                      className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg p-3 mt-1 h-32"
                      placeholder="Onbeperkte bestellingen&#10;Pushprinter integratie&#10;Bezorgzones"
                    />
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPlanDialog(false)}>Annuleren</Button>
                <Button onClick={handleSavePlan} disabled={savingPlan} className="bg-emerald-600 hover:bg-emerald-700">
                  {savingPlan ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Opslaan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {activeTab === 'overview' && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Omzet per Plan</h3>
            <div className="space-y-4">
              {[
                { id: 'starter', name: 'FoodFlows', color: 'emerald' },
                { id: 'enterprise', name: 'Enterprise', color: 'amber' },
              ].map((plan) => (
                <div key={plan.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full bg-${plan.color}-500`}></div>
                    <span className="text-white font-medium">{plan.name}</span>
                  </div>
                  <span className="text-lg font-bold text-white">
                    {formatPrice(revenue?.revenue_by_plan?.[plan.id] || 0, 'EUR')}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Abonnees per Plan</h3>
            <div className="space-y-4">
              {[
                { id: 'free', name: 'Gratis', color: 'gray' },
                { id: 'starter', name: 'FoodFlows', color: 'emerald' },
                { id: 'enterprise', name: 'Enterprise', color: 'amber' },
              ].map((plan) => (
                <div key={plan.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full bg-${plan.color}-500`}></div>
                    <span className="text-white font-medium">{plan.name}</span>
                  </div>
                  <span className="text-lg font-bold text-white">
                    {revenue?.subscribers_by_plan?.[plan.id] || 0}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'subscribers' && (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Restaurant</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Plan</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Totaal Betaald</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Vervalt</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {subscribers.map((sub) => (
                <tr key={sub.id} className="hover:bg-gray-700/30">
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white font-medium">{sub.name}</p>
                      <p className="text-gray-400 text-sm">{sub.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${planColors[sub.plan] || planColors.free}`}>
                      {sub.plan}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white font-medium">
                    {formatPrice(sub.total_paid || 0, 'EUR')}
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-sm">
                    {sub.end_date ? new Date(sub.end_date).toLocaleDateString('nl-BE') : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      sub.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {sub.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Datum</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Plan</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Bedrag</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(revenue?.recent_transactions || []).map((tx, idx) => (
                <tr key={idx} className="hover:bg-gray-700/30">
                  <td className="px-6 py-4 text-gray-400 text-sm">
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString('nl-BE', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    }) : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${planColors[tx.plan_id] || planColors.free}`}>
                      {tx.plan_id}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-white font-medium">
                    {formatPrice(tx.amount || 0, tx.currency || 'EUR')}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      tx.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {tx.status}
                    </span>
                  </td>
                </tr>
              ))}
              {(!revenue?.recent_transactions || revenue.recent_transactions.length === 0) && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-gray-400">
                    Nog geen transacties
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Admin Domains Page
export const AdminDomainsPage = () => {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    try {
      const response = await adminAPI.getDomains();
      setDomains(response.data);
    } catch (error) {
      toast.error('Kon domeinen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const verifyDomain = async (restaurantId) => {
    try {
      await adminAPI.verifyDomain(restaurantId);
      toast.success('Domein geactiveerd');
      fetchDomains();
    } catch (error) {
      toast.error('Kon domein niet activeren');
    }
  };

  const removeDomain = async (restaurantId) => {
    if (!confirm('Weet je zeker dat je dit domein wilt verwijderen?')) return;
    try {
      await adminAPI.removeDomain(restaurantId);
      toast.success('Domein verwijderd');
      fetchDomains();
    } catch (error) {
      toast.error('Kon domein niet verwijderen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-domains-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Domein Beheer</h1>
        <p className="text-gray-400 mt-1">Beheer custom domeinen van restaurants</p>
      </div>

      {/* DNS Instructions */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
        <h3 className="text-blue-400 font-semibold mb-2">Workflow voor Custom Domeinen</h3>
        <ol className="text-blue-300 text-sm space-y-2 list-decimal list-inside mb-3">
          <li>
            Restaurant zet de DNS-records klaar bij zijn registrar:
            <div className="mt-1.5 ml-5 space-y-1 font-mono text-xs">
              <div>
                <span className="text-blue-200">www</span>
                <span className="text-blue-400/60"> → CNAME → </span>
                <code className="bg-blue-500/20 px-2 py-0.5 rounded">food-flows.com</code>
              </div>
              <div>
                <span className="text-blue-200">het kale domein</span>
                <span className="text-blue-400/60"> → ALIAS of ANAME → </span>
                <code className="bg-blue-500/20 px-2 py-0.5 rounded">food-flows.com</code>
              </div>
              <div className="text-blue-400/60">
                kan de registrar geen ALIAS? Dan een A-record naar{' '}
                <code className="bg-blue-500/20 px-2 py-0.5 rounded">167.233.78.236</code>
              </div>
            </div>
          </li>
          <li>Restaurant klikt op "DNS Verifiëren" — jij krijgt een e-mail zodra de DNS klopt</li>
          <li>
            Iemand opent <span className="font-mono">https://hetdomein.be</span> in de browser.
            Het SSL-certificaat wordt op dat moment automatisch aangevraagd en het domein
            activeert zichzelf. Meestal binnen enkele seconden.
          </li>
        </ol>
        <p className="text-blue-300/70 text-xs">
          De "Activeren"-knop hieronder is een handmatige terugval voor als stap 3 niet
          vanzelf gaat. Normaal hoef je hem niet te gebruiken.
        </p>
        <p className="text-amber-300/80 text-xs mt-2">
          Let op: een kaal domein kan volgens de DNS-standaard geen CNAME hebben. Zonder
          ALIAS, ANAME of A-record blijft <span className="font-mono">domein.be</span>
          {' '}onbereikbaar terwijl <span className="font-mono">www.domein.be</span> wel werkt.
          Dat is nu bij beide actieve domeinen het geval.
        </p>
      </div>

      {domains.length === 0 ? (
        <div className="bg-gray-800 rounded-2xl p-12 border border-gray-700 text-center">
          <Globe className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">Nog geen custom domeinen geconfigureerd</p>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Domein</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Restaurant</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Geverifieerd</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {domains.map((domain) => (
                <tr key={domain.restaurant_id} className="hover:bg-gray-700/30">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <span className="text-white font-medium">{domain.domain}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white">{domain.restaurant_name}</p>
                      <p className="text-gray-400 text-sm">/{domain.slug}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit ${
                      domain.active ? 'bg-emerald-500/20 text-emerald-400' : domain.verified ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'
                    }`} data-testid={`domain-status-${domain.restaurant_id}`}>
                      {domain.active ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {domain.active ? 'Actief' : domain.verified ? 'DNS OK — activeren' : 'Wacht op DNS'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-gray-400 text-sm">
                    {domain.verified_at ? new Date(domain.verified_at).toLocaleDateString('nl-BE') : '-'}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {!domain.active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => verifyDomain(domain.restaurant_id)}
                          className="text-emerald-400 hover:text-emerald-300"
                          title="Domein activeren"
                          data-testid={`activate-domain-${domain.restaurant_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Activeren
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDomain(domain.restaurant_id)}
                        className="text-red-400 hover:text-red-300"
                        title="Domein verwijderen"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Admin Invoices Page — uitsluitend de abonnementsfacturen die restaurants aan
// het platform betalen. Betalingen van eindklanten aan restaurants stonden hier
// eerder tussen; die horen onder Bestellingen.
export const AdminInvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [zoek, setZoek] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getInvoices(0, 200, statusFilter || null);
      setInvoices(response.data.invoices || []);
      setTotals(response.data.totals || {});
    } catch (e) {
      toast.error('Kon facturen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const datum = (v) => (v ? new Date(v).toLocaleDateString('nl-BE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) : '—');

  const periode = (inv) => {
    if (!inv.period_start) return '—';
    if (!inv.period_end) return datum(inv.period_start);
    return `${datum(inv.period_start)} – ${datum(inv.period_end)}`;
  };

  const STATUS = {
    paid: { label: 'Betaald', cls: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' },
    open: { label: 'Openstaand', cls: 'bg-amber-500/20 text-amber-400 border-amber-500/40' },
    failed: { label: 'Mislukt', cls: 'bg-red-500/20 text-red-400 border-red-500/40' },
  };

  const filtered = invoices.filter((inv) => {
    if (!zoek.trim()) return true;
    const q = zoek.toLowerCase();
    return (inv.restaurant_name || '').toLowerCase().includes(q)
      || (inv.number || '').toLowerCase().includes(q)
      || (inv.restaurant_email || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="admin-invoices-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Abonnementsfacturen</h1>
        <p className="text-gray-400 mt-1">
          Wat restaurants aan FoodFlows betalen. Bestellingen van eindklanten staan onder Bestellingen.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-6">
          <Euro className="w-8 h-8 text-emerald-100 mb-3" />
          <p className="text-3xl font-bold text-white">{formatPrice(totals.net || 0, 'EUR')}</p>
          <p className="text-emerald-100 text-sm">Omzet excl. btw</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <Percent className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-3xl font-bold text-white">{formatPrice(totals.vat || 0, 'EUR')}</p>
          <p className="text-gray-400 text-sm">Af te dragen btw</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <CreditCard className="w-8 h-8 text-purple-400 mb-3" />
          <p className="text-3xl font-bold text-white">{formatPrice(totals.total || 0, 'EUR')}</p>
          <p className="text-gray-400 text-sm">Geïnd incl. btw</p>
        </div>
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <FileText className="w-8 h-8 text-blue-400 mb-3" />
          <p className="text-3xl font-bold text-white">{totals.paid_count || 0}</p>
          <p className="text-gray-400 text-sm">
            Betaald · {totals.open_count || 0} open · {totals.failed_count || 0} mislukt
          </p>
        </div>
      </div>

      <div className="flex gap-4 flex-wrap">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="invoice-status-filter"
          className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2"
        >
          <option value="">Alle statussen</option>
          <option value="paid">Betaald</option>
          <option value="open">Openstaand</option>
          <option value="failed">Mislukt</option>
        </select>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            placeholder="Zoek op restaurant of factuurnummer"
            className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg pl-9 pr-4 py-2"
          />
        </div>
        <Button variant="outline" onClick={fetchInvoices} className="text-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Vernieuwen
        </Button>
      </div>

      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Factuurnr.</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Restaurant</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Periode</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Plan</th>
              <th className="px-4 py-4 text-right text-sm font-medium text-gray-300">Excl. btw</th>
              <th className="px-4 py-4 text-right text-sm font-medium text-gray-300">Btw</th>
              <th className="px-4 py-4 text-right text-sm font-medium text-gray-300">Incl. btw</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Status</th>
              <th className="px-4 py-4 text-right text-sm font-medium text-gray-300">Factuur</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Laden…
              </td></tr>
            )}
            {!loading && filtered.map((inv) => {
              const st = STATUS[inv.status] || STATUS.open;
              return (
                <tr key={inv.id} className="hover:bg-gray-700/30" data-testid={`invoice-${inv.id}`}>
                  <td className="px-4 py-4">
                    <p className="text-white font-mono text-sm">{inv.number || '—'}</p>
                    <p className="text-gray-500 text-xs">{datum(inv.created_at)}</p>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-white font-medium">{inv.restaurant_name}</p>
                    <p className="text-gray-400 text-xs">{inv.restaurant_email}</p>
                  </td>
                  <td className="px-4 py-4 text-gray-300 text-sm whitespace-nowrap">{periode(inv)}</td>
                  <td className="px-4 py-4 text-gray-300 text-sm">{inv.plan_name}</td>
                  <td className="px-4 py-4 text-right text-gray-300">
                    {formatPrice(inv.amount_net || 0, inv.currency || 'EUR')}
                  </td>
                  <td className="px-4 py-4 text-right text-gray-400 text-sm">
                    {formatPrice(inv.amount_vat || 0, inv.currency || 'EUR')}
                    <span className="block text-xs text-gray-600">
                      {Math.round((inv.vat_rate || 0) * 100)}%
                    </span>
                  </td>
                  <td className="px-4 py-4 text-right text-white font-semibold">
                    {formatPrice(inv.amount_total || 0, inv.currency || 'EUR')}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs border ${st.cls}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {inv.hosted_invoice_url ? (
                        <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                           title="Open in Stripe"
                           className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : (
                        <span className="text-gray-600 text-xs" title="Van voor de overstap naar Stripe-abonnementen">
                          geen Stripe-factuur
                        </span>
                      )}
                      {inv.invoice_pdf && (
                        <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer" title="PDF"
                           className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300">
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => { setSelectedInvoice(inv); setShowDetails(true); }}
                        className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300" title="Details">
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center">
                  <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-300 font-medium">Nog geen abonnementsfacturen</p>
                  <p className="text-gray-500 text-sm mt-1 max-w-md mx-auto">
                    Zodra een restaurant een betaald abonnement afsluit, maakt Stripe hiervoor
                    elke periode een genummerde factuur aan en verschijnt die hier.
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Factuur {selectedInvoice?.number || ''}
            </DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm">Restaurant</p>
                  <p className="text-white">{selectedInvoice.restaurant_name}</p>
                  <p className="text-gray-500 text-xs">{selectedInvoice.restaurant_email}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Plan</p>
                  <p className="text-white">{selectedInvoice.plan_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Periode</p>
                  <p className="text-white">{periode(selectedInvoice)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Status</p>
                  <p className="text-white">{(STATUS[selectedInvoice.status] || STATUS.open).label}</p>
                </div>
              </div>
              <div className="border-t border-gray-700 pt-4 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Excl. btw</span>
                  <span className="text-white">{formatPrice(selectedInvoice.amount_net || 0, 'EUR')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">
                    Btw {Math.round((selectedInvoice.vat_rate || 0) * 100)}%
                  </span>
                  <span className="text-white">{formatPrice(selectedInvoice.amount_vat || 0, 'EUR')}</span>
                </div>
                <div className="flex justify-between font-bold border-t border-gray-700 pt-2 mt-2">
                  <span className="text-gray-300">Totaal</span>
                  <span className="text-white">{formatPrice(selectedInvoice.amount_total || 0, 'EUR')}</span>
                </div>
              </div>
              {selectedInvoice.hosted_invoice_url && (
                <a href={selectedInvoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 text-sm">
                  <ExternalLink className="w-4 h-4" /> Bekijk deze factuur in Stripe
                </a>
              )}
              <p className="text-gray-500 text-xs">
                De betaalstatus komt rechtstreeks van Stripe en is hier niet handmatig te wijzigen.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const AdminOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchOrders();
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllOrders(0, 100, statusFilter || null);
      setOrders(response.data.orders);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Kon bestellingen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    confirmed: 'bg-blue-500/20 text-blue-400',
    preparing: 'bg-purple-500/20 text-purple-400',
    ready: 'bg-emerald-500/20 text-emerald-400',
    delivered: 'bg-gray-500/20 text-gray-400',
    cancelled: 'bg-red-500/20 text-red-400',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Alle Bestellingen</h1>
          <p className="text-gray-400 mt-1">{total} bestellingen totaal</p>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-700 border border-gray-600 text-white rounded-lg px-4 py-2"
        >
          <option value="">Alle statussen</option>
          <option value="pending">In afwachting</option>
          <option value="confirmed">Bevestigd</option>
          <option value="preparing">In bereiding</option>
          <option value="ready">Klaar</option>
          <option value="delivered">Bezorgd</option>
          <option value="cancelled">Geannuleerd</option>
        </select>
      </div>

      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Bestelling</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Restaurant</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Klant</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Totaal</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-300">Datum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {orders.map((order) => (
              <tr key={order.id} className="hover:bg-gray-700/30">
                <td className="px-6 py-4">
                  <span className="text-white font-medium">{order.order_number}</span>
                </td>
                <td className="px-6 py-4 text-gray-300">{order.restaurant_name}</td>
                <td className="px-6 py-4">
                  <div>
                    <p className="text-white">{order.customer_name}</p>
                    <p className="text-gray-400 text-sm">{order.customer_phone}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-white font-medium">{formatPrice(order.total, order.currency)}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[order.status] || 'bg-gray-500/20 text-gray-400'}`}>
                    {order.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {new Date(order.created_at).toLocaleDateString('nl-BE', { 
                    day: '2-digit', 
                    month: 'short', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Admin Settings Page
export const AdminSettingsPage = () => {
  const [settings, setSettings] = useState({
    platform_name: 'FoodFlows',
    support_email: 'support@food-flows.com',
    default_commission_rate: 0,
    trial_days: 14,
    allow_registrations: true,
    maintenance_mode: false,
    announcement: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminAPI.getSettings();
      setSettings({ ...settings, ...response.data });
    } catch (error) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Instellingen opgeslagen');
    } catch (error) {
      toast.error('Kon instellingen niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Platform Instellingen</h1>
        <p className="text-gray-400 mt-1">Configureer het FoodFlows platform</p>
      </div>

      <div className="grid gap-6">
        {/* General Settings */}
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-6">Algemeen</h3>
          <div className="grid gap-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-300">Platform Naam</Label>
                <Input
                  value={settings.platform_name}
                  onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Support Email</Label>
                <Input
                  type="email"
                  value={settings.support_email}
                  onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-300">Trial Periode (dagen)</Label>
                <Input
                  type="number"
                  value={settings.trial_days}
                  onChange={(e) => setSettings({ ...settings, trial_days: parseInt(e.target.value) || 0 })}
                  className="bg-gray-700 border-gray-600 text-white"
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Commissie Percentage</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={settings.default_commission_rate}
                    onChange={(e) => setSettings({ ...settings, default_commission_rate: parseFloat(e.target.value) || 0 })}
                    className="bg-gray-700 border-gray-600 text-white"
                    min={0}
                    max={100}
                    step={0.5}
                  />
                  <span className="text-gray-400">%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toggles */}
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-6">Platform Status</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-xl">
              <div>
                <p className="text-white font-medium">Registraties Toestaan</p>
                <p className="text-gray-400 text-sm">Nieuwe restaurants kunnen zich registreren</p>
              </div>
              <Switch
                checked={settings.allow_registrations}
                onCheckedChange={(checked) => setSettings({ ...settings, allow_registrations: checked })}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-xl">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 ${settings.maintenance_mode ? 'text-amber-400' : 'text-gray-500'}`} />
                <div>
                  <p className="text-white font-medium">Onderhoudsmodus</p>
                  <p className="text-gray-400 text-sm">Platform tijdelijk uitschakelen voor onderhoud</p>
                </div>
              </div>
              <Switch
                checked={settings.maintenance_mode}
                onCheckedChange={(checked) => setSettings({ ...settings, maintenance_mode: checked })}
              />
            </div>
          </div>
        </div>

        {/* Announcement */}
        <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-4">Platform Aankondiging</h3>
          <p className="text-gray-400 text-sm mb-4">
            Toon een bericht aan alle ingelogde restaurant gebruikers
          </p>
          <Textarea
            value={settings.announcement || ''}
            onChange={(e) => setSettings({ ...settings, announcement: e.target.value })}
            placeholder="Typ hier een aankondiging..."
            className="bg-gray-700 border-gray-600 text-white"
            rows={3}
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Opslaan...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Instellingen Opslaan
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
